import {
  ApiError,
  getOrgBranding,
  requestLogoUploadUrl,
  updateOrgBranding,
} from '@jq4race/api-client';
import { LocalizedError, useTranslation } from '@jq4race/i18n';
import type { OrgBrandingResponse } from '@jq4race/shared';
import { Button, Card, CardContent, Input, Label } from '@jq4race/ui';
import { CheckCircle2, TriangleAlert, Upload, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';

// ── Logo file validation ───────────────────────────────────────────────────────

const MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024; // 2 MB
const MIN_DIMENSION_PX = 200;
const ACCEPTED_TYPES = ['image/png', 'image/jpeg'] as const;
type AcceptedContentType = (typeof ACCEPTED_TYPES)[number];

type FileValidationError =
  | { kind: 'invalid-type' }
  | { kind: 'size-exceeded'; sizeMb: string }
  | { kind: 'too-small'; width: number; height: number };

function humanizeValidationError(
  t: (key: string, opts?: Record<string, unknown>) => string,
  err: FileValidationError,
): string {
  if (err.kind === 'invalid-type') return t('validation.invalidType');
  if (err.kind === 'size-exceeded') return t('validation.sizeExceeded', { sizeMb: err.sizeMb });
  return t('validation.tooSmall', {
    width: err.width,
    height: err.height,
    minDimension: MIN_DIMENSION_PX,
  });
}

async function validateLogoFile(file: File): Promise<FileValidationError | null> {
  if (!ACCEPTED_TYPES.includes(file.type as AcceptedContentType)) {
    return { kind: 'invalid-type' };
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return { kind: 'size-exceeded', sizeMb: (file.size / (1024 * 1024)).toFixed(1) };
  }
  try {
    const bitmap = await createImageBitmap(file);
    const { width, height } = bitmap;
    bitmap.close();
    if (width < MIN_DIMENSION_PX || height < MIN_DIMENSION_PX) {
      return { kind: 'too-small', width, height };
    }
  } catch {
    // If createImageBitmap is unavailable (e.g. test env), fall through without
    // blocking the upload — dimension check is best-effort in unsupported envs.
  }
  return null;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function BrandingPage() {
  const { t } = useTranslation('org-branding');
  const [branding, setBranding] = useState<OrgBrandingResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);

  // Form state
  const [primaryColor, setPrimaryColor] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getOrgBranding();
      setBranding(data);
      setPrimaryColor(data.primaryColor ?? '#1c3557');
    } catch (e) {
      setErrorBanner(translateApiError(e, t, { fallback: t('loadError') }));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  // Auto-dismiss success banner
  useEffect(() => {
    if (!success) return;
    const id = window.setTimeout(() => setSuccess(null), 5000);
    return () => window.clearTimeout(id);
  }, [success]);

  // Clean up object URL on unmount or file change
  useEffect(() => {
    return () => {
      if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);
    };
  }, [filePreviewUrl]);

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    setFileError(null);
    setSelectedFile(null);
    if (filePreviewUrl) {
      URL.revokeObjectURL(filePreviewUrl);
      setFilePreviewUrl(null);
    }
    if (!file) return;

    const validationError = await validateLogoFile(file);
    if (validationError) {
      setFileError(humanizeValidationError(t, validationError));
      // Reset the input so the user can try again with the same filename
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setSelectedFile(file);
    setFilePreviewUrl(URL.createObjectURL(file));
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setErrorBanner(null);
    setSaving(true);

    try {
      let logoKey: string | undefined;

      if (selectedFile) {
        const contentType = selectedFile.type as AcceptedContentType;
        const { uploadUrl, key } = await requestLogoUploadUrl(contentType);
        const putRes = await fetch(uploadUrl, {
          method: 'PUT',
          body: selectedFile,
          headers: { 'Content-Type': contentType },
        });
        if (!putRes.ok) {
          throw new LocalizedError(t('uploadError', { status: putRes.status }));
        }
        logoKey = key;
      }

      const body: { primaryColor?: string; logoKey?: string } = {};
      if (primaryColor) body.primaryColor = primaryColor;
      if (logoKey) body.logoKey = logoKey;

      // At least one field is required by the API schema
      if (!body.primaryColor && !body.logoKey) {
        setErrorBanner(t('saveErrorRequired'));
        return;
      }

      const saved = await updateOrgBranding(body);
      setBranding(saved);
      setSelectedFile(null);
      if (filePreviewUrl) {
        URL.revokeObjectURL(filePreviewUrl);
        setFilePreviewUrl(null);
      }
      if (fileInputRef.current) fileInputRef.current.value = '';
      setSuccess(t('saveSuccess'));
    } catch (e) {
      if (e instanceof ApiError) {
        setErrorBanner(translateApiError(e, t, { fallback: t('saveErrorGeneric') }));
      } else {
        setErrorBanner(translateApiError(e, t, { fallback: t('saveErrorUnexpected') }));
      }
    } finally {
      setSaving(false);
    }
  }

  const currentLogoUrl = filePreviewUrl ?? branding?.logoUrl ?? null;

  return (
    <div className="space-y-6">
      <p className="max-w-xl text-[14px] leading-relaxed text-jq-graphite">{t('intro')}</p>

      {errorBanner && (
        <FeedbackBanner tone="error" message={errorBanner} onDismiss={() => setErrorBanner(null)} />
      )}
      {success && (
        <FeedbackBanner tone="success" message={success} onDismiss={() => setSuccess(null)} />
      )}

      <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
        <CardContent className="p-6 sm:p-8">
          {loading ? (
            <p className="text-[13px] text-jq-graphite">{t('loading')}</p>
          ) : (
            <form onSubmit={handleSave} className="space-y-8">
              {/* Logo section */}
              <div className="space-y-4">
                <div>
                  <Label className="mb-1.5 block text-[13px] font-medium text-jq-charcoal">
                    {t('logo.label')}
                  </Label>
                  <p className="text-[12px] text-jq-graphite">{t('logo.hint')}</p>
                </div>

                <div className="flex items-start gap-6">
                  {/* Preview */}
                  <div className="h-20 w-20 shrink-0 overflow-hidden rounded-md border border-jq-sand/70 bg-jq-surface-1 flex items-center justify-center">
                    {currentLogoUrl ? (
                      <img
                        src={currentLogoUrl}
                        alt={t('logo.previewAlt')}
                        className="h-full w-full object-contain"
                      />
                    ) : (
                      <span className="text-[11px] text-jq-stone text-center leading-tight px-2">
                        {t('logo.noLogo')}
                      </span>
                    )}
                  </div>

                  <div className="flex-1 space-y-2">
                    <label
                      htmlFor="logo-file"
                      className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-jq-sand/70 bg-jq-surface-1 px-3 py-2 text-[13px] font-medium text-jq-charcoal shadow-sm transition hover:bg-jq-surface-2"
                    >
                      <Upload className="h-4 w-4 shrink-0" strokeWidth={1.75} />
                      {selectedFile ? t('logo.changeFile') : t('logo.chooseFile')}
                    </label>
                    <Input
                      ref={fileInputRef}
                      id="logo-file"
                      type="file"
                      accept="image/png,image/jpeg"
                      disabled={saving}
                      onChange={handleFileChange}
                      className="sr-only"
                    />
                    {selectedFile && (
                      <p className="text-[12px] text-jq-graphite">{selectedFile.name}</p>
                    )}
                    {fileError && (
                      <p role="alert" className="text-[12px] text-jq-danger">
                        {fileError}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Primary color section */}
              <div className="space-y-3">
                <Label
                  htmlFor="primary-color-picker"
                  className="block text-[13px] font-medium text-jq-charcoal"
                >
                  {t('color.label')}
                </Label>
                <p className="text-[12px] text-jq-graphite">{t('color.hint')}</p>
                <div className="flex items-center gap-3">
                  <input
                    id="primary-color-picker"
                    type="color"
                    disabled={saving}
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    className="h-10 w-14 cursor-pointer rounded border border-jq-sand/70 bg-transparent p-0.5"
                    aria-label={t('color.pickerAriaLabel')}
                  />
                  <Input
                    id="primary-color-hex"
                    type="text"
                    disabled={saving}
                    value={primaryColor}
                    onChange={(e) => {
                      const v = e.target.value;
                      // Accept partial input; sync to picker only when value is a valid hex
                      setPrimaryColor(v);
                    }}
                    maxLength={7}
                    placeholder="#1c3557"
                    className="w-32 font-mono text-[13px]"
                    aria-label={t('color.hexAriaLabel')}
                  />
                  <span
                    className="h-8 w-8 rounded-full border border-jq-sand/70 shadow-sm"
                    style={{
                      backgroundColor: /^#[0-9A-Fa-f]{6}$/.test(primaryColor)
                        ? primaryColor
                        : undefined,
                    }}
                    aria-hidden="true"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 border-t border-jq-sand/70 pt-6">
                <Button type="submit" size="sm" disabled={saving}>
                  {saving ? t('saving') : t('save')}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Feedback banner ────────────────────────────────────────────────────────────

interface FeedbackBannerProps {
  tone: 'error' | 'success';
  message: string | null;
  onDismiss: () => void;
}

function FeedbackBanner({ tone, message, onDismiss }: FeedbackBannerProps) {
  const { t } = useTranslation('org-branding');
  if (!message) return null;
  const Icon = tone === 'success' ? CheckCircle2 : TriangleAlert;
  const palette =
    tone === 'success'
      ? 'border-jq-success/40 bg-jq-success/10 text-jq-success'
      : 'border-jq-danger/40 bg-jq-danger/10 text-jq-danger';
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-3 rounded-sm border px-4 py-3 text-[13px] ${palette}`}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('dismiss')}
        className="-mr-1 -mt-1 rounded p-1 opacity-70 transition hover:opacity-100"
      >
        <X className="h-4 w-4" strokeWidth={1.75} />
      </button>
    </div>
  );
}
